import { createContext, Dispatch, FC, SetStateAction, useContext, useEffect, useState } from 'react';
import { AuthContext } from './AuthProvider';
import { getAllStripeSubscriptions, listOrderRequests, listPresignRequests, listStudios } from './RequestsApi';
import { listUserGroups, listUsers } from './UsersApi';
import { listCustomers } from './CustomersApi';
import { Users, Utils, Orders, Templates, Studio, Customers } from '@hellohair/types';
import { getAllDoctorsFromStudio, getFirstDateOfMonthsAgo } from '../helpers/requests';

interface DataContextSchema {
  formDetailsState: [
    Utils.DBItem<Templates.SurveyTemplate> | undefined,
    Dispatch<SetStateAction<Utils.DBItem<Templates.SurveyTemplate> | undefined>>,
  ];
  // Customer Requests
  ordersState: [Utils.DBItem<Orders.OrderRequest>[], Dispatch<SetStateAction<Utils.DBItem<Orders.OrderRequest>[]>>];
  presignRequestsState: [{ requestId: string;email: string; createdAt: number, orderState: string }[], Dispatch<SetStateAction<{ requestId: string;email: string; createdAt: number, orderState: string }[]>>];
  orderDetailsState: [
    Utils.DBItem<Orders.OrderRequestDetails> | undefined,
    Dispatch<SetStateAction<Utils.DBItem<Orders.OrderRequestDetails> | undefined>>,
  ];
  // Backend users
  usersState: [Users.BackendUser[], Dispatch<SetStateAction<Users.BackendUser[]>>];
  userDetailsState: [Users.BackendUser | undefined, Dispatch<SetStateAction<Users.BackendUser | undefined>>];
  userGroupsState: [Users.UserGroup[], Dispatch<SetStateAction<Users.UserGroup[]>>];
  customersState: [Customers.ShopCustomer[], Dispatch<SetStateAction<Customers.ShopCustomer[]>>];
  prescriptionState: [Users.PrescriptionState | null, Dispatch<SetStateAction<Users.PrescriptionState | null>>];
  subscriptionsState: [Users.Subscription[] | null, Dispatch<SetStateAction<Users.Subscription[] | null>>];
  studiosState: [Studio[], Dispatch<SetStateAction<Studio[]>>];
  oldSubscriptionsState: [Users.OldSubscription[], Dispatch<SetStateAction<Users.OldSubscription[]>>];
}

const dummyFunction = () => {
  console.error('Should not have happened');
};

export const DataContext = createContext<DataContextSchema>({
  formDetailsState: [undefined, dummyFunction],
  ordersState: [[], dummyFunction],
  presignRequestsState: [[], dummyFunction],
  orderDetailsState: [undefined, dummyFunction],
  usersState: [[], dummyFunction],
  userDetailsState: [undefined, dummyFunction],
  userGroupsState: [[], dummyFunction],
  customersState: [[], dummyFunction],
  prescriptionState: [null, dummyFunction],
  subscriptionsState: [null, dummyFunction],
  studiosState: [[], dummyFunction],
  oldSubscriptionsState: [[], dummyFunction],
});

interface Props {
  children: React.ReactNode;
}

const DataProvider: FC<Props> = ({ children }) => {
  const formDetailsState = useState<Utils.DBItem<Templates.SurveyTemplate> | undefined>();
  const ordersState = useState<Utils.DBItem<Orders.OrderRequest>[]>([]);
  const [, setOrders] = ordersState;
  
  const presignRequestsState = useState<{ requestId: string; email: string; createdAt: number, orderState: string }[]>([]);
  const [, setPresignRequests] = presignRequestsState;

  const orderDetailsState = useState<Utils.DBItem<Orders.OrderRequestDetails> | undefined>();

  const usersState = useState<Users.BackendUser[]>([]);
  const [users, setUsers] = usersState;

  const customersState = useState<Customers.ShopCustomer[]>([]);
  const [, setCustomers] = customersState;

  const userDetailsState = useState<Users.BackendUser | undefined>();
  //const [userDetails, setUserDetails] = userDetailsState;

  const userGroupsState = useState<Users.UserGroup[]>([]);
  const [, setUserGroups] = userGroupsState;

  const prescriptionState = useState<Users.PrescriptionState | null>(null);
  // const [, setPrescriptionState] = prescriptionState;

  const subscriptionsState = useState<Users.Subscription[] | null>(null);

  const studiosState = useState<Studio[]>([]);
  const [studios, setStudios] = studiosState;

  const oldSubscriptionsState = useState<Users.OldSubscription[]>([]);
  const [, setOldSubscriptions] = oldSubscriptionsState;

  const { apiClient, userAttributes } = useContext(AuthContext);

  useEffect(() => {
    if (apiClient && userAttributes && studios.length > 0 && users.length > 0) {
      // TODO: provide default doctor id from environment variable
      const isProduction = window.location.hostname === 'doc.hellohair.com';
      const defaultDoctorId = isProduction
        ? '0394e8a2-a031-7035-cd05-38c3cfa9ade5' // info@ifue-haartransplantation.de (prod)
        : 'f314d822-f011-705b-6b37-4cc79561dc71'; // andreas@hellohair.com (dev)

      const managingStudio = studios.find((studio) => studio.managerId === userAttributes?.id);
      let monthsAgo = getFirstDateOfMonthsAgo(2);

      let doctorIds: string[] | undefined = undefined;

      if (userAttributes?.id === defaultDoctorId) {
        doctorIds = [];
        console.warn('return all requests without assigned doctor: ');
      } else if (userAttributes?.roles?.includes('admin')) {
        console.warn('return all requests for admin: ');
      } else if (studios.find((studio) => studio.managerId === userAttributes?.id)) {
        monthsAgo = getFirstDateOfMonthsAgo(1);
        doctorIds = getAllDoctorsFromStudio(users, managingStudio || null).map((d) => d.sub);
        console.warn('return all requests for manager: ', userAttributes?.id);
      } else if (userAttributes?.roles?.includes('doctor')) {
        doctorIds = [userAttributes.id];
        console.warn('return all requests for doctor: ');
      }

      listOrderRequests({
        apiClient,
        fetchPolicy: 'network-only',
        from: monthsAgo.toString(),
        doctorIds,
      }).then((data) => {
        console.warn('requests: ', data);
        setOrders(data);
      });

      // Fetch presign requests
      listPresignRequests(apiClient, 'network-only').then((data) => {
        console.warn('presign requests: ', data);
        setPresignRequests(data);
      });
    }
  }, [apiClient, userAttributes, studios, users]);

  useEffect(() => {
    if (!apiClient) {
      setUsers([]);
      setCustomers([]);
      setUserGroups([]);
      setStudios([]);
      return;
    }

    Promise.all([
      listUsers(apiClient),
      listStudios(apiClient),
      listUserGroups(apiClient),
      listCustomers(apiClient),
    ]).then(([u, s, ug, c]) => {
      setUsers(u);
      setStudios(s);
      setUserGroups(ug);
      setCustomers(c);

      Promise.all([getAllStripeSubscriptions(apiClient)]).then(([os]) => {
        setOldSubscriptions(os as Users.OldSubscription[]);
      });
    });
  }, [apiClient]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <DataContext.Provider
      value={{
        formDetailsState: formDetailsState,
        ordersState,
        presignRequestsState,
        orderDetailsState,
        usersState,
        userDetailsState,
        userGroupsState,
        customersState,
        prescriptionState,
        subscriptionsState,
        studiosState,
        oldSubscriptionsState,
      }}
    >
      {children}
    </DataContext.Provider>
  );
};

export default DataProvider;
